import React, { createContext, useContext, useState, useEffect } from 'react';
import API from '../services/api';
import { initSocket, disconnectSocket } from '../services/socket';
import { generateKeyPair, hasKeyPair, getLocalPublicKey } from '../services/encryption';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser]             = useState(null);
  const [redirectTo, setRedirectTo] = useState(null);
  const [loading, setLoading]       = useState(true);

  // ── Setup E2E keys for a user ────────────────────────────
  const setupEncryption = async (token) => {
    try {
      if (!hasKeyPair()) {
        // Generate new RSA key pair — private key stays in browser
        const { publicKeyPem } = await generateKeyPair();
        // Send public key to server
        await API.post('/auth/keys', { publicKey: publicKeyPem });
      } else {
        // Keys exist — make sure server has the public key
        const publicKey = getLocalPublicKey();
        if (publicKey) {
          await API.post('/auth/keys', { publicKey });
        }
      }
    } catch (err) {
      console.warn('E2E setup warning:', err.message);
    }
  };

  // ── Restore session ──────────────────────────────────────
  useEffect(() => {
    const token    = localStorage.getItem('blixo_token');
    const redirect = localStorage.getItem('blixo_redirect');
    if (token && redirect) {
      API.get('/auth/me')
        .then(async ({ data }) => {
          setUser(data.user);
          setRedirectTo(data.redirectTo);
          initSocket(token);
          await setupEncryption(token);
        })
        .catch(() => {
          localStorage.removeItem('blixo_token');
          localStorage.removeItem('blixo_redirect');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // ── Login ────────────────────────────────────────────────
  const login = async (email, password) => {
    const { data } = await API.post('/auth/login', { email, password });
    localStorage.setItem('blixo_token', data.token);
    localStorage.setItem('blixo_redirect', data.redirectTo);
    setUser(data.user);
    setRedirectTo(data.redirectTo);
    initSocket(data.token);
    // Setup E2E encryption keys after login
    await setupEncryption(data.token);
    return data;
  };

  // ── Register ─────────────────────────────────────────────
  const register = async (name, email, password) => {
    const { data } = await API.post('/auth/register', { name, email, password });
    localStorage.setItem('blixo_token', data.token);
    localStorage.setItem('blixo_redirect', data.redirectTo);
    setUser(data.user);
    setRedirectTo(data.redirectTo);
    initSocket(data.token);
    await setupEncryption(data.token);
    return data;
  };

  // ── Logout ───────────────────────────────────────────────
  const logout = () => {
    localStorage.removeItem('blixo_token');
    localStorage.removeItem('blixo_redirect');
    // Note: we keep E2E keys in localStorage so user can still decrypt old messages
    // To fully clear: also call clearKeyPair() from encryption.js
    disconnectSocket();
    setUser(null);
    setRedirectTo(null);
  };

  return (
    <AuthContext.Provider value={{ user, redirectTo, login, register, logout, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
